import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Megaphone, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import Reveal from "@/components/Reveal";

const imgUrl = (item) =>
  item.has_upload ? `${API}/carousel/image/${item.id}` : item.image_url;

export default function HomeCarousel() {
  const { data: items = [] } = useQuery({
    queryKey: ["carousel"],
    queryFn: async () => (await api.get("/carousel")).data,
  });

  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(false);
  const count = items.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);

  useEffect(() => {
    if (count <= 1 || zoom) return;
    const t = setInterval(next, 5500);
    return () => clearInterval(t);
  }, [count, next, zoom]);

  useEffect(() => {
    if (index >= count && count > 0) setIndex(0);
  }, [count, index]);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, prev, next]);

  if (count === 0) return null;
  const current = items[Math.min(index, count - 1)];

  return (
    <section data-testid="home-carousel" className="relative py-16 sm:py-24 bg-sec-1 overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-chapel-red">
              <Megaphone className="w-4 h-4" /> Featured
            </span>
            <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight">
              What&apos;s Happening?
            </h2>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black/60 aspect-[4/3] sm:aspect-[16/10]">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="absolute inset-0"
              >
                {/* Blurred backdrop fills the frame so portrait/square flyers look intentional */}
                <img
                  src={imgUrl(current)}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40"
                />
                {/* Full flyer shown uncropped */}
                <img
                  src={imgUrl(current)}
                  alt={current.caption || "Announcement"}
                  onClick={() => setZoom(true)}
                  data-testid="carousel-image"
                  className="relative w-full h-full object-contain cursor-zoom-in"
                />
                {current.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
                    <p className="text-white font-heading font-bold text-base sm:text-xl tracking-tight max-w-3xl">
                      {current.caption}
                    </p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {count > 1 && (
              <>
                <button
                  onClick={prev}
                  data-testid="carousel-prev"
                  aria-label="Previous"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-black/40 hover:bg-chapel-red text-white backdrop-blur-sm transition-colors duration-300"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={next}
                  data-testid="carousel-next"
                  aria-label="Next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-black/40 hover:bg-chapel-red text-white backdrop-blur-sm transition-colors duration-300"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="absolute bottom-4 right-4 flex gap-2">
                  {items.map((it, i) => (
                    <button
                      key={it.id}
                      onClick={() => setIndex(i)}
                      data-testid={`carousel-dot-${i}`}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        i === index ? "w-7 bg-chapel-red" : "w-2.5 bg-white/50 hover:bg-white/80"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </Reveal>
      </div>

      {/* Zoom lightbox */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            data-testid="carousel-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0B0B0F]/95 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setZoom(false)}
          >
            <button
              data-testid="carousel-lightbox-close"
              onClick={() => setZoom(false)}
              className="absolute top-5 right-5 w-11 h-11 grid place-items-center rounded-full bg-white/10 border border-white/20 hover:bg-chapel-red transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            {count > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Previous"
                className="absolute left-3 sm:left-8 w-11 h-11 grid place-items-center rounded-full bg-white/10 border border-white/20 hover:bg-chapel-red transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
            )}
            <motion.div
              key={current.id}
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl max-h-[88vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imgUrl(current)}
                alt={current.caption || "Announcement"}
                className="max-h-[82vh] w-auto rounded-2xl object-contain"
              />
              {current.caption && (
                <p className="mt-4 text-center text-white/85">{current.caption}</p>
              )}
            </motion.div>
            {count > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Next"
                className="absolute right-3 sm:right-8 w-11 h-11 grid place-items-center rounded-full bg-white/10 border border-white/20 hover:bg-chapel-red transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
